import React, { useEffect, useState } from 'react'
import { Box, Button, Card, Divider, Heading, Stack, StackItem, Tag, Text } from '@indeed/ifl-components'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import type { DefaultRootState } from 'react-redux'
import { useFormatDateTime } from 'syft-acp-core/lib/hooks'

import {
  getAuthoredReasonLabel,
  getLineStateLabel,
  SchedulingPlanDialog,
  useSchedulingPlan,
} from 'syft-acp-core/components/SchedulingPlanDialog'
import { fetchAgencyJobsHistory } from 'syft-acp-core/store/agency-jobs-history/actions'
import {
  selectAgencyJobsHistory,
  selectAgencyJobsHistoryStatus,
} from 'syft-acp-core/store/agency-jobs-history/selectors'
import type {
  AgencySchedulingPlanSummary,
  AgencyJobHistoryEntry,
} from 'syft-acp-core/store/agency-jobs-history/types'

import {
  formatAgencyRank,
  formatShiftCount,
  getAgencyJobStatuses,
  getAgencyJobStatusTag,
  isLiveAgencyJob,
} from './helpers'

const dateTimeFormat = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
} as const

// Offset of the rail from the list's inline start; markers are centred on it.
const RAIL_OFFSET = '0.5rem'
const MARKER_SIZE = '1rem'

// Draws the rail through every entry and stops it at the last marker.
const timelineItemSx = {
  position: 'relative',
  paddingInlineStart: 6,
  paddingBlockEnd: 5,
  '&::before': {
    content: '""',
    position: 'absolute',
    insetInlineStart: RAIL_OFFSET,
    insetBlockStart: MARKER_SIZE,
    insetBlockEnd: 0,
    borderInlineStartWidth: '2px',
    borderInlineStartStyle: 'solid',
    borderInlineStartColor: 'border.weak',
    transform: 'translateX(-50%)',
  },
  '&:last-of-type': { paddingBlockEnd: 0 },
  '&:last-of-type::before': { display: 'none' },
} as const

const timelineListSx = { listStyle: 'none', margin: 0, padding: 0 } as const

type TimelineMarkerProps = { isLive?: boolean }

const TimelineMarker = ({ isLive = false }: TimelineMarkerProps) => (
  <Box
    aria-hidden="true"
    sx={{
      position: 'absolute',
      insetInlineStart: RAIL_OFFSET,
      insetBlockStart: '0.2rem',
      inlineSize: MARKER_SIZE,
      blockSize: MARKER_SIZE,
      borderRadius: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: isLive ? 'background.action' : 'border.strong',
    }}
  />
)

const SECTION_HEADING_ID = 'agency-jobs-history-heading'

type DetailProps = { term: string; children: React.ReactNode }

const Detail = ({ term, children }: DetailProps) => (
  <Box sx={{ minInlineSize: '10rem' }}>
    <Text as="dt" level={2} variant="secondary">
      {term}
    </Text>
    <Text as="dd" level={3} sx={{ margin: 0 }}>
      {children}
    </Text>
  </Box>
)

type PlanActionProps = {
  agencyJob: AgencyJobHistoryEntry
  jobId: string
  plan: AgencySchedulingPlanSummary
}

const PlanAction = ({ agencyJob, jobId, plan }: PlanActionProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { requestPlan } = useSchedulingPlan(plan.id)

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-label={`View plan v${plan.version} for ${agencyJob.agency_name}, agency job ${agencyJob.agency_job_id}`}
        onClick={() => {
          requestPlan()
          setIsOpen(true)
        }}
      >
        View plan
      </Button>
      {isOpen && (
        <SchedulingPlanDialog
          agencyName={agencyJob.agency_name}
          jobId={jobId}
          onOpenChange={setIsOpen}
          planId={plan.id}
          venueTimezone={agencyJob.venue_timezone}
        />
      )}
    </>
  )
}

type PlanVersionsProps = {
  agencyJob: AgencyJobHistoryEntry
  jobId: string
  shiftId?: string
  formatDate: (value: string) => string
}

const PlanVersions = ({ agencyJob, jobId, shiftId, formatDate }: PlanVersionsProps) => {
  const headingId = `agency-job-${agencyJob.agency_job_id}-plans-heading`

  return (
    <Box sx={{ marginBlockStart: 4 }}>
      <Text as="h4" id={headingId} level={2} variant="secondary" sx={{ margin: 0, marginBlockEnd: 1 }}>
        Booking plan versions
      </Text>
      <Box as="ol" aria-labelledby={headingId} sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {agencyJob.agency_cascade_scheduling_plans.map(plan => {
          // Filtered by shift, the lines hold only that shift's line.
          const shiftLine = plan.agency_cascade_scheduling_plan_shifts[0]

          return (
            <Box
              as="li"
              key={plan.id}
              sx={{
                paddingBlock: 2,
                borderBlockStartWidth: '1px',
                borderBlockStartStyle: 'solid',
                borderBlockStartColor: 'border.weak',
              }}
            >
              <Stack distribution="between" alignment="center" spacing={3} wrap>
                <Box>
                  <Text as="div" level={3}>
                    {`v${plan.version} · ${getAuthoredReasonLabel(plan.authored_reason)} · ${formatDate(
                      plan.authored_at,
                    )} · ${formatShiftCount(plan.agency_cascade_scheduling_plan_shifts_count)}`}
                  </Text>
                  {shiftId && (
                    <Text as="div" level={2} variant="secondary">
                      {shiftLine
                        ? `Shift #${shiftId} deadline: ${
                            shiftLine.deadline_at ? formatDate(shiftLine.deadline_at) : 'none'
                          } · ${getLineStateLabel(shiftLine.state)}`
                        : `Shift #${shiftId} is not in this version`}
                    </Text>
                  )}
                </Box>
                <PlanAction agencyJob={agencyJob} jobId={jobId} plan={plan} />
              </Stack>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

type TimelineEntryProps = { agencyJob: AgencyJobHistoryEntry; jobId: string; shiftId?: string }

const TimelineEntry = ({ agencyJob, jobId, shiftId }: TimelineEntryProps) => {
  const [, formatDateTime] = useFormatDateTime()
  const format = { ...dateTimeFormat, timeZone: agencyJob.venue_timezone }
  const formatDate = (value: string) => formatDateTime(value, { format })
  const hasPlans = agencyJob.agency_cascade_scheduling_plans.length > 0
  const headingId = `agency-job-${agencyJob.agency_job_id}-heading`
  const isLive = isLiveAgencyJob(agencyJob)

  return (
    <Box as="li" sx={timelineItemSx}>
      <TimelineMarker isLive={isLive} />
      <Text level={2} variant="secondary" sx={{ display: 'block' }}>
        <time dateTime={agencyJob.offered_at}>{`Allocated ${formatDate(agencyJob.offered_at)}`}</time>
      </Text>
      <Card as="article" aria-labelledby={headingId} sx={{ marginBlockStart: 2 }}>
        <Stack distribution="between" alignment="start" sx={{ marginBlockEnd: 3 }}>
          <Box>
            <Text as="h3" id={headingId} level={3} sx={{ margin: 0 }}>
              {agencyJob.agency_name}
            </Text>
            <Text level={2} variant="secondary">
              {`${formatAgencyRank(agencyJob.agency_rank)} · agency job #${agencyJob.agency_job_id}`}
            </Text>
          </Box>
          {/*
            Grows into the rest of the row, so the badges keep to the card's end both beside the heading and
            on a row of their own once they no longer fit beside it. At the narrowest widths they wrap onto
            further rows, and a badge wider than the card wraps its own text.
          */}
          <StackItem sx={{ flexGrow: 1 }}>
            <Stack spacing={2} distribution="end">
              {getAgencyJobStatuses(agencyJob).map(status => {
                const statusTag = getAgencyJobStatusTag(status)

                return (
                  <Tag key={status} status={statusTag.tagStatus}>
                    {statusTag.label}
                  </Tag>
                )
              })}
            </Stack>
          </StackItem>
        </Stack>
        {/* Not a Stack: Stack always renders a div, and a dl may only hold one div level around its dt/dd pairs. */}
        <Box as="dl" sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 3, margin: 0 }}>
          {agencyJob.accepted_at && <Detail term="Accepted">{formatDate(agencyJob.accepted_at)}</Detail>}
          {agencyJob.declined_at && <Detail term="Declined">{formatDate(agencyJob.declined_at)}</Detail>}
          {agencyJob.ended_at && !agencyJob.declined_at && (
            <Detail term="Ended">{formatDate(agencyJob.ended_at)}</Detail>
          )}
          {isLive && agencyJob.accepted_at && hasPlans && (
            <Detail term="Next enforced deadline">
              {agencyJob.next_evaluation_at ? formatDate(agencyJob.next_evaluation_at) : 'None scheduled'}
            </Detail>
          )}
          {/* Most agency jobs never get a plan, so its absence is stated without guessing at a reason. */}
          {!hasPlans && !agencyJob.declined_at && <Detail term="Booking plan">None</Detail>}
        </Box>
        {hasPlans && (
          <PlanVersions agencyJob={agencyJob} jobId={jobId} shiftId={shiftId} formatDate={formatDate} />
        )}
      </Card>
    </Box>
  )
}

type Props = {
  jobId: string
  /** Narrows the timeline to agency jobs that had spots on this shift, with its deadline per version. */
  shiftId?: string
}

/**
 * The history of every agency job of a job, accepted or not, with its plan versions, as a timeline in
 * the order the API returns them (newest first). The agency shifts list only shows the current holder,
 * so this is where earlier agencies and their plans stay visible after a cascade.
 *
 * Renders nothing until the history loads with at least one agency job, so a job never offered to an
 * agency, a job ID that does not exist and a failed load all leave the page as it was before.
 */
export const AgencyJobsHistory = ({ jobId, shiftId }: Props) => {
  const dispatch = useDispatch()
  // No shift_id key when absent, so the job-wide list keeps its own entity set.
  const query = shiftId ? { job_id: jobId, shift_id: shiftId } : { job_id: jobId }
  const agencyJobs = useSelector(
    (state: DefaultRootState) => selectAgencyJobsHistory(state, query),
    shallowEqual,
  )
  const status = useSelector((state: DefaultRootState) => selectAgencyJobsHistoryStatus(state, query))

  useEffect(() => {
    // A loaded or in-flight set is reused. A failed one is requested again, as nothing was shown for it.
    if (status === 'idle' || status === 'failed') dispatch(fetchAgencyJobsHistory(query))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, shiftId])

  if (status !== 'loaded' || agencyJobs.length === 0) return null

  return (
    <Box
      as="section"
      aria-labelledby={SECTION_HEADING_ID}
      // The ACP page centres its text; the timeline reads down its left edge.
      sx={{ marginBlockEnd: 6, textAlign: 'left' }}
    >
      <Stack distribution="between" alignment="end" spacing={3} wrap sx={{ marginBlockEnd: 3 }}>
        <Box>
          <Heading as="h2" id={SECTION_HEADING_ID} level={6} sx={{ margin: 0 }}>
            Allocations Per Agency
          </Heading>
          <Text as="p" level={3} variant="secondary" sx={{ margin: 0, marginBlockStart: 1 }}>
            {shiftId
              ? `Agencies offered shift #${shiftId}, newest first, with their booking plan versions.`
              : `Agencies offered job #${jobId}, newest first, with their booking plan versions.`}
          </Text>
        </Box>
        <Text level={3} variant="secondary">
          {`${agencyJobs.length} agency job${agencyJobs.length === 1 ? '' : 's'}`}
        </Text>
      </Stack>
      <Divider sx={{ marginBlockEnd: 5 }} />

      <Box as="ol" aria-label="Agency jobs, newest first" sx={timelineListSx}>
        {agencyJobs.map(agencyJob => (
          <TimelineEntry
            key={agencyJob.agency_job_id}
            agencyJob={agencyJob}
            jobId={jobId}
            shiftId={shiftId}
          />
        ))}
      </Box>
    </Box>
  )
}
